import { useEffect, useRef, useState } from "react";

const SearchableSelect = ({ value, onChange, onBlur, options = [], placeholder = "-- Select --", isDisabled = false, dropdownMaxHeight = "102px" }) => {
    const [inputText, setInputText] = useState("");
    const [filtered, setFiltered] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        setInputText(value?.label || "");
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setInputText(value?.label || "");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [value]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const item = listRef.current.children[highlightedIndex];
            if (item) item.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex]);

    const handleInputChange = (e) => {
        const text = e.target.value;
        setInputText(text);
        setHighlightedIndex(-1);

        if (!text) {
            setFiltered(options);
            setIsOpen(true);
            onChange(null);
            return;
        }

        const results = options.filter((o) =>
            o.label.toLowerCase().includes(text.toLowerCase())
        );
        setFiltered(results);
        setIsOpen(true);
    };

    const handleFocus = () => {
        if (isDisabled) return;
        setFiltered(options);
        setIsOpen(true);
    };

    const handleKeyDown = (e) => {
        if (!isOpen || filtered.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0) handleSelect(filtered[highlightedIndex]);
        } else if (e.key === "Escape") {
            setIsOpen(false);
            setHighlightedIndex(-1);
            setInputText(value?.label || "");
        }
    };

    const handleSelect = (item) => {
        setInputText(item.label);
        onChange(item);
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="form-control"
                    placeholder={placeholder}
                    value={inputText}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    onBlur={onBlur}
                    disabled={isDisabled}
                    autoComplete="off"
                    style={isDisabled ? { backgroundColor: "#e9ecef", cursor: "not-allowed" } : {}}
                />
            </div>

            {isOpen && !isDisabled && (
                <ul
                    ref={listRef}
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        backgroundColor: "#fff",
                        border: "1px solid #ced4da",
                        borderRadius: "0 0 4px 4px",
                        maxHeight: dropdownMaxHeight,
                        overflowY: "auto",
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                    }}
                >
                    {filtered.length === 0 ? (
                        <li style={{ padding: "8px 12px", color: "#888", fontSize: "13px" }}>
                            No options found
                        </li>
                    ) : (
                        filtered.map((item, index) => (
                            <li
                                key={item.value}
                                onMouseDown={() => handleSelect(item)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onMouseLeave={() => setHighlightedIndex(-1)}
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    borderBottom: "1px solid #f0f0f0",
                                    backgroundColor: highlightedIndex === index ? "#c8e0ff" : "#fff"
                                }}
                            >
                                {item.label}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
};

export default SearchableSelect;
